import { css } from 'styled-components';
import 'react-datepicker/dist/react-datepicker.css';
import { getScrollBarStyles } from '../config/styles';
import {
  END_DATE_CLASS_NAME,
  IGNORE_SELECTED_DATE_CLASS_NAME,
} from '../components/Builder/DatePickerWidget/DateRangePicker/DateRangePickerUI';
import { theme } from './theme';
import { defaultInputStyles } from '../components/Form/inputStyles';
import { floatingComponentStyles, optionHover } from './shareableStyles';

const reactDatePickerStyles = css`
  .react-datepicker-popper {
    ${floatingComponentStyles};
    &[data-placement^='bottom'] {
      padding-top: 2px;
    }
    &[data-placement^='top'] {
      padding-bottom: 2px;
    }
  }

  .react-datepicker {
    display: block;
    border: 0;
    border-radius: 2px;
    box-shadow: unset;
    background-color: unset;
    font: inherit;

    .react-datepicker__triangle {
      display: none;
    }

    .react-datepicker__navigation {
      top: 15px;
      width: 16px;
      height: 16px;
      outline: none !important;
    }

    .react-datepicker__navigation--previous {
      left: 192px;
    }

    .react-datepicker__navigation--next {
      left: 210px;
    }

    .react-datepicker__navigation-icon {
      position: static;
      display: inline-block;
      width: 8px;
      height: 8px;
      border-right: 2px solid ${theme.colors.secondary600};
      border-bottom: 2px solid ${theme.colors.secondary600};
      font-size: 0;
      &::before {
        content: none;
      }
    }

    .react-datepicker__navigation:hover .react-datepicker__navigation-icon {
      border-color: ${theme.colors.mainBlue};
    }

    .react-datepicker__navigation-icon--previous {
      transform: rotate(-225deg);
    }

    .react-datepicker__navigation-icon--next {
      transform: rotate(-45deg);
    }

    .react-datepicker__navigation--previous--disabled
      .react-datepicker__navigation-icon,
    .react-datepicker__navigation--previous--disabled:hover
      .react-datepicker__navigation-icon,
    .react-datepicker__navigation--next--disabled
      .react-datepicker__navigation-icon,
    .react-datepicker__navigation--next--disabled:hover
      .react-datepicker__navigation-icon {
      border-color: ${theme.colors.secondary400};
    }

    .react-datepicker__navigation--previous--disabled:hover,
    .react-datepicker__navigation--next--disabled:hover {
      cursor: default;
    }

    .react-datepicker__month-container {
      width: 240px;
      padding: 12px 9px;
      float: unset;
    }

    .react-datepicker__header {
      padding: 0;
      border: 0;
      background-color: transparent;
      cursor: default;
    }

    .react-datepicker-year-header,
    .react-datepicker__current-month {
      padding: 0 36px 12px 7px;
      text-align: left;
      font-size: 14px;
      font-weight: 600;
      line-height: 22px;
      color: ${theme.colors.secondary900};
    }

    .react-datepicker__day-names {
      display: grid;
      grid-auto-flow: column;
      grid-auto-columns: 1fr;
      column-gap: 8px;
      height: 24px;
      margin-bottom: 0;
      border-radius: 2px;
      background-color: ${theme.colors.level1};
    }

    .react-datepicker__day-name {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      margin: 0;
      text-transform: uppercase;
      font-size: 12px;
      font-weight: 600;
      line-height: normal;
      color: ${theme.colors.secondary800};
    }

    .react-datepicker__month {
      margin: 0;
      border: 0;
    }

    .react-datepicker__week {
      display: grid;
      grid-auto-flow: column;
      grid-auto-columns: 1fr;
      column-gap: 8px;
      margin-top: 4px;
    }
  }

  .react-datepicker__input-container input {
    ${defaultInputStyles};
  }

  .react-datepicker__year--container {
    width: 240px;
    padding: 16px 9px 12px;
  }

  .react-datepicker__year {
    margin: 0;
  }

  .react-datepicker__year-wrapper {
    max-width: 100%;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    column-gap: 8px;
    row-gap: 4px;
  }

  .react-datepicker__month-text--keyboard-selected {
    background-color: transparent;
  }

  .react-datepicker__month .react-datepicker__month-text,
  .react-datepicker__year .react-datepicker__year-text {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: auto;
    height: 24px;
    margin: 0;
    border-radius: 2px;
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    color: ${theme.colors.secondary800};
    &:hover {
      border-radius: 2px;
      background-color: ${theme.colors.blue500};
      outline: 1px solid ${theme.colors.mainBlue} !important;
      color: ${theme.colors.secondary800};
    }
  }
  .react-datepicker__month .react-datepicker__month--selected,
  .react-datepicker__month .react-datepicker__month--selected:hover,
  .react-datepicker__year .react-datepicker__year-text--selected,
  .react-datepicker__year .react-datepicker__year-text--selected:hover {
    border-radius: 2px;
    background-color: ${theme.colors.mainBlue};
    color: #fff;
    outline: none !important;
  }

  .react-datepicker__month .react-datepicker__month-text--today,
  .react-datepicker__year .react-datepicker__year-text--today {
    outline: 1px solid ${theme.colors.mainBlue};
  }

  .react-datepicker__monthPicker {
    display: flex;
    flex-direction: column;
    row-gap: 4px;

    .react-datepicker__month-wrapper {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      grid-column-gap: 8px;
    }
  }

  .react-datepicker__time-container {
    width: 100%;
    float: unset;
  }

  .react-datepicker__time-container
    .react-datepicker__time
    .react-datepicker__time-box {
    width: 100%;
    text-align: left;
    border-bottom-right-radius: 2px;
  }

  .react-datepicker__time-box .react-datepicker__time-list {
    ${getScrollBarStyles(4)}
  }

  .react-datepicker__time-container
    .react-datepicker__time
    .react-datepicker__time-box
    ul.react-datepicker__time-list
    li.react-datepicker__time-list-item {
    height: 28px;
    padding: 5px 8px;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: normal;
    color: ${theme.colors.secondary800};
    &:hover {
      ${optionHover};
      cursor: pointer;
    }
  }

  .react-datepicker__time-container
    .react-datepicker__time
    .react-datepicker__time-box
    ul.react-datepicker__time-list
    li.react-datepicker__time-list-item--selected,
  .react-datepicker__time-container
    .react-datepicker__time
    .react-datepicker__time-box
    ul.react-datepicker__time-list
    li.react-datepicker__time-list-item--selected:hover {
    ${optionHover};
  }

  .react-datepicker__time-container .react-datepicker__time {
    background: unset;
  }

  .react-datepicker__day {
    width: 100%;
    height: 100%;
    margin: 0;
    border-radius: 0;
    font-size: 12px;
    font-weight: 500;
    text-align: center;
    line-height: 24px;
    color: ${theme.colors.secondary800};
    user-select: none !important;
    outline: none;
    &.react-datepicker__day--outside-month {
      color: ${theme.colors.secondary400};
    }
    &:hover {
      border-radius: 2px;
      background-color: ${theme.colors.blue500};
      outline: 1px solid ${theme.colors.mainBlue} !important;
      color: ${theme.colors.secondary800};
    }
    &.react-datepicker__day--in-range {
      position: relative;
      background-color: ${theme.colors.blue500};
      color: ${theme.colors.secondary600};
      &:hover {
        background-color: ${theme.colors.blue500};
        border-radius: 0;
        color: ${theme.colors.mainBlue};
        outline: none !important;
      }
      &::after {
        content: '';
        background-color: ${theme.colors.blue500};
        width: 9px;
        height: 100%;
        position: absolute;
        left: 100%;
      }
    }

    &.react-datepicker__day--range-start,
    &.react-datepicker__day--range-start:hover,
    &.react-datepicker__day--range-end,
    &.react-datepicker__day--range-end:hover {
      border-radius: 2px;
      background-color: ${theme.colors.mainBlue};
      color: #fff;
      &.${IGNORE_SELECTED_DATE_CLASS_NAME} {
        border-radius: 0;
        background-color: ${theme.colors.blue500};
        color: ${theme.colors.secondary600};
        &:hover {
          background-color: ${theme.colors.blue500};
          border-radius: 0;
          color: ${theme.colors.mainBlue};
          outline: none !important;
        }
      }
    }

    &.react-datepicker__day--disabled,
    &.react-datepicker__day--disabled:hover {
      color: ${theme.colors.secondary300};
    }

    &.react-datepicker__day--selected,
    &.react-datepicker__day--selected:hover {
      border-radius: 2px;
      background-color: ${theme.colors.mainBlue};
      color: #fff;
      outline: none !important;
      &.${END_DATE_CLASS_NAME} {
        background-color: #fd7e14 !important;
      }
    }

    &.react-datepicker__day--range-start::before {
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      background-color: rgba(138, 148, 189, 0.1);
      width: 12px;
      height: 100%;
    }

    &.react-datepicker__day--range-end::after {
      content: none;
    }
    &.react-datepicker__day:last-child::after {
      background-color: transparent;
    }

    &.${IGNORE_SELECTED_DATE_CLASS_NAME} {
      ::before {
        width: auto !important; // a glich in the datepicker library
      }
    }
  }
`;

export default reactDatePickerStyles;
